import * as React from "react";

const EIGENAKQUISE = "Eigenakquise";

type UseContactOriginsParams = {
  formFields: Record<string, unknown>;
  updateField: (name: string, value: string) => void;
  onClearReferrer: () => void;
  onClearTippgeber: () => void;
  onClearKundenverbindung: () => void;
  /**
   * `false` schaltet den Hook still: keine Default-Vorauswahl, kein Schreiben
   * in `contactOriginSelections`. Gebraucht beim Umwandeln eines bestehenden
   * Kontakts — die Herkunft gehört zur Anlage, und der Default-Effekt würde das
   * Formular ungefragt als geändert markieren.
   */
  enabled?: boolean;
};

const getInitialOrigins = (formFields: Record<string, unknown>) => {
  try {
    const raw = (formFields as any)["contactOriginSelections"];
    if (raw) {
      const parsed = JSON.parse(raw as string);
      if (Array.isArray(parsed)) {
        return parsed.filter((v) => typeof v === "string");
      }
    }
  } catch { }

  const initial: string[] = [];
  if ((formFields as any)["eigenakquiseInteressent"] || (formFields as any)["eigenakquise"]) {
    initial.push("Eigenakquise");
  }
  if ((formFields as any)["referrerInteressent"]) initial.push("Empfehlungsgeber");
  if ((formFields as any)["tippgeberInteressent"]) initial.push("Tippgeber");
  if ((formFields as any)["kundenverbindungInteressent"]) initial.push("Verknüpfung zu bestehendem Kontakt");
  if ((formFields as any)["netzwerkInteressent"]) initial.push("Netzwerk");
  if (((formFields as any)["contactOriginOther"] ?? "").toString().trim().length > 0) {
    initial.push("Sonstiges");
  }
  return initial;
};

export const useContactOrigins = ({
  formFields,
  updateField,
  onClearReferrer,
  onClearTippgeber,
  onClearKundenverbindung,
  enabled = true,
}: UseContactOriginsParams) => {
  const [contactOrigins, setContactOrigins] = React.useState<string[]>(() => getInitialOrigins(formFields));
  const [contactOriginOther, setContactOriginOther] = React.useState<string>(
    ((formFields as any)["contactOriginOther"] as string) ?? "",
  );

  const setContactOriginOtherValue = React.useCallback((value: string) => {
    setContactOriginOther(value);
    updateField("contactOriginOther", value);
  }, [updateField]);

  // Default: "Eigenakquise" vorauswählen, wenn beim ersten Mount nichts gewählt
  // ist. Bei Edit-Flows mit vorbelegtem JSON ist contactOrigins bereits gefüllt
  // → kein Override. Leeres [] (User wählt Eigenakquise bewusst ab) wird nicht
  // re-defaulted, da der Effekt nur einmal beim Mount läuft.
  React.useEffect(() => {
    if (enabled && contactOrigins.length === 0) {
      const next = [EIGENAKQUISE];
      setContactOrigins(next);
      updateField("contactOriginSelections", JSON.stringify(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleContactOrigin = React.useCallback((option: string, checked: boolean) => {
    setContactOrigins((prev) => {
      let next: string[];
      if (checked) {
        if (option === EIGENAKQUISE) {
          // Eigenakquise schließt alle anderen Herkunfts-Optionen aus.
          next = [EIGENAKQUISE];
        } else {
          // Andere Option: Eigenakquise entfernen, übrige bleiben multi-select.
          next = [...new Set([...prev.filter((v) => v !== EIGENAKQUISE), option])];
        }
      } else {
        next = prev.filter((v) => v !== option);
      }
      updateField("contactOriginSelections", JSON.stringify(next));
      return next;
    });

    if (checked && option === EIGENAKQUISE) {
      // Geister-States der anderen Herkünfte leeren, da deren Checkboxen nun raus
      // sind (sonst bleiben Referrer/Tippgeber/Verknüpfung/Sonstiges unsichtbar
      // im State stehen).
      onClearReferrer();
      onClearTippgeber();
      onClearKundenverbindung();
      setContactOriginOther("");
      updateField("referrerInteressent", "");
      updateField("tippgeberInteressent", "");
      updateField("kundenverbindungInteressent", "");
      updateField("herkunftEmpfehlungsgeber", "");
      updateField("herkunftTippgeber", "");
      updateField("contactOriginOther", "");
    }

    if (!checked && option === "Empfehlungsgeber") {
      onClearReferrer();
      updateField("referrerInteressent", "");
      updateField("herkunftEmpfehlungsgeber", "");
    }
    if (!checked && option === "Tippgeber") {
      onClearTippgeber();
      updateField("tippgeberInteressent", "");
      updateField("herkunftTippgeber", "");
    }
    if (!checked && option === "Verknüpfung zu bestehendem Kontakt") {
      onClearKundenverbindung();
      updateField("kundenverbindungInteressent", "");
    }
    if (!checked && option === "Sonstiges") {
      setContactOriginOther("");
      updateField("contactOriginOther", "");
    }
  }, [onClearKundenverbindung, onClearReferrer, onClearTippgeber, updateField]);

  return {
    contactOrigins,
    contactOriginOther,
    toggleContactOrigin,
    setContactOriginOtherValue,
  };
};

